import { ImagePlus, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../../../components/ui/common/button";

interface ProductImageUploadProps {
  /** Persisted image to show as the starting thumbnail (edit flow only — a new product has none yet). */
  initialImageUrl?: string | null;
  onUpload: (file: File) => void;
  /** Fires when the current image (persisted or just-picked) is removed. Omit if there's nothing to persist yet. */
  onRemove?: () => void;
}

/**
 * Tailwind-native product image upload — preview box + upload/remove
 * buttons, no Semantic UI. Shared by both the register flow (no
 * `initialImageUrl`/`onRemove`, a locally-picked file held until the product
 * is created) and the edit flow (`initialImageUrl` set from the persisted
 * product, `onRemove` wired to the real delete mutation) — previously two
 * near-duplicate components, consolidated into this one after the edit
 * flow's copy turned out to be broken (see `ProductEditInfoCard.tsx`).
 */
const ProductImageUpload = ({ initialImageUrl, onUpload, onRemove }: ProductImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialImageUrl ?? null);
  const isObjectUrlRef = useRef(false);

  useEffect(() => {
    setPreviewUrl(prev => {
      if (isObjectUrlRef.current && prev) URL.revokeObjectURL(prev);
      return initialImageUrl ?? null;
    });
    isObjectUrlRef.current = false;
  }, [initialImageUrl]);

  // Revoke any locally-created object URL on unmount so picking several
  // images in a row (or navigating away) doesn't leak blob URLs.
  useEffect(() => {
    return () => {
      if (isObjectUrlRef.current && previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (file: File) => {
    setPreviewUrl(prev => {
      if (isObjectUrlRef.current && prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    isObjectUrlRef.current = true;
    onUpload(file);
  };

  const handleRemove = () => {
    setPreviewUrl(prev => {
      if (isObjectUrlRef.current && prev) URL.revokeObjectURL(prev);
      return null;
    });
    isObjectUrlRef.current = false;
    onRemove?.();
  };

  return (
    <div className="tw-flex tw-items-center tw-gap-4">
      <div className="tw-flex tw-h-16 tw-w-16 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border tw-border-dashed tw-border-border tw-bg-muted/40">
        {previewUrl ? (
          <img src={previewUrl} alt="Product" className="tw-h-full tw-w-full tw-object-cover" />
        ) : (
          <ImagePlus className="tw-h-5 tw-w-5 tw-text-muted-foreground" />
        )}
      </div>
      <div className="tw-flex tw-gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
          <Upload className="tw-h-4 tw-w-4" />
          Upload an image
        </Button>
        {previewUrl ? (
          <Button type="button" variant="outline" size="sm" onClick={handleRemove}>
            <Trash2 className="tw-h-4 tw-w-4" />
          </Button>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="tw-hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleSelect(file);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default ProductImageUpload;
